import React, { useState, useEffect, useCallback, useMemo } from "react";
import { RefreshCw } from "lucide-react";
import { fetchLocationsByOfficeCodes } from "../Manage-Team/api";
import { selectablePointLocations } from "../../../utils/locationHierarchy";
import MyYardTab from "./myyardtab";
import YardSettingsTab from "./yardsettingstab";
import TrailersTab, { STATUS_FILTER_OPTIONS } from "./TrailersTab";
import TrailerDetailView from "./TrailerDetailView";
import { YardCheckInSearchModal } from "./YardCheckInSearchModal";
import { AppointmentCheckInFormModal } from "./AppointmentCheckInFormModal";
import { appointmentTitleId } from "./checkInGateUtils";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import { usePrivilege } from "../../../guard/usePrivilege";

const DEFAULT_TABS_COUNT = 2;

// The YMS module fully unmounts when navigating to another top-level page (standard SPA
// routing behavior), which wipes all local state including open trailer/check-in tabs.
// Persist the open-tab list across that unmount/remount the same way
// yardCheckInTrailerGrid.js already persists pending check-in rows.
const OPEN_TABS_STORAGE_KEY = "yard_open_tabs_v1";

function readStoredTabs() {
  try {
    const raw = sessionStorage.getItem(OPEN_TABS_STORAGE_KEY);
    if (!raw) return { trailerTabs: [], checkInTabs: [], checkoutTabs: [], activeTab: "overview" };
    const parsed = JSON.parse(raw);
    return {
      trailerTabs: Array.isArray(parsed?.trailerTabs) ? parsed.trailerTabs : [],
      checkInTabs: Array.isArray(parsed?.checkInTabs) ? parsed.checkInTabs : [],
      checkoutTabs: Array.isArray(parsed?.checkoutTabs) ? parsed.checkoutTabs : [],
      activeTab: typeof parsed?.activeTab === "string" ? parsed.activeTab : "overview",
    };
  } catch {
    return { trailerTabs: [], checkInTabs: [], checkoutTabs: [], activeTab: "overview" };
  }
}

const YardManagement = () => {
  const { hasPrivilege } = usePrivilege();
  const canCheckInTrailer = hasPrivilege("YMS:CHECKIN_POST");
  const [storedTabs] = useState(readStoredTabs);
  const [activeTab, setActiveTab] = useState(() => storedTabs.activeTab);
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [trailerTabs, setTrailerTabs] = useState(() => storedTabs.trailerTabs);
  const [checkInTabs, setCheckInTabs] = useState(() => storedTabs.checkInTabs);
  const [checkoutTabs, setCheckoutTabs] = useState(() => storedTabs.checkoutTabs);
  const [trailersRefreshKey, setTrailersRefreshKey] = useState(0);
  const [myYardRefreshKey, setMyYardRefreshKey] = useState(0);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    try {
      return localStorage.getItem("yard_auto_refresh_enabled") !== "false";
    } catch {
      return true;
    }
  });
  const [refreshIntervalSec, setRefreshIntervalSec] = useState(() => {
    try {
      const stored = Number(localStorage.getItem("yard_auto_refresh_interval_sec"));
      return Number.isFinite(stored) && stored > 0 ? stored : 20;
    } catch {
      return 20;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("yard_auto_refresh_enabled", String(autoRefreshEnabled));
    } catch {
      // ignore storage failures (e.g. private browsing)
    }
  }, [autoRefreshEnabled]);

  useEffect(() => {
    try {
      localStorage.setItem("yard_auto_refresh_interval_sec", String(refreshIntervalSec));
    } catch {
      // ignore storage failures (e.g. private browsing)
    }
  }, [refreshIntervalSec]);
  const [checkInSearchOpen, setCheckInSearchOpen] = useState(false);
  const [toolbarReloading, setToolbarReloading] = useState(false);

  const [yardSearchQuery, setYardSearchQuery] = useState("");
  const [yardSearchTrigger, setYardSearchTrigger] = useState(0);
  const [yardStatusFilter, setYardStatusFilter] = useState("All Conditions");
  const [yardFilteredCount, setYardFilteredCount] = useState(0);

  const tabDefs = useMemo(() => {
    const rows = [
      { id: "overview", name: "Overview" },
      { id: "myYard", name: "My Yard" },
      ...trailerTabs.map((t) => ({ id: t.id, name: t.trailer.trailerNumber })),
      // A walk-up draft has no appointment id to name the tab after (LGX-1101).
      ...checkInTabs.map((t) => ({
        id: t.id,
        name: t.appointment?.manualEntry
          ? "Check In — Walk-Up"
          : `Check In ${appointmentTitleId(t.appointment)}`,
      })),
      ...checkoutTabs.map((t) => ({ id: t.id, name: `Check Out ${t.trailer.trailerNumber}` })),
    ];
    if (settingsOpen) rows.push({ id: "yardSettings", name: "Yard Settings" });
    return rows;
  }, [trailerTabs, checkInTabs, checkoutTabs, settingsOpen]);

  const activeTabIndex = Math.max(0, tabDefs.findIndex((t) => t.id === activeTab));

  useEffect(() => {
    if (tabDefs.length && !tabDefs.some((x) => x.id === activeTab)) {
      setActiveTab("overview");
    }
  }, [tabDefs, activeTab]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        OPEN_TABS_STORAGE_KEY,
        JSON.stringify({ trailerTabs, checkInTabs, checkoutTabs, activeTab })
      );
    } catch {
      /* best-effort persistence only */
    }
  }, [trailerTabs, checkInTabs, checkoutTabs, activeTab]);

  useEffect(() => {
    const load = async () => {
      setIsLoadingLocations(true);
      try {
        const res = await fetchLocationsByOfficeCodes();
        const all = res.data ?? [];
        // reconstructLocationList first repairs any entries the backend's identity-reference
        // (Jackson @JsonIdentityInfo) collapsed to a bare id number, then only top-level (POINT)
        // locations belong in this dropdown — same as MyAppointments.js's equivalent picker.
        // WAREHOUSE rows are children of a location and get populated separately once a location
        // is selected; GEOGRAPHY rows are internal rate-shopping graph nodes, never user-facing.
        // Unnamed POINTs (shipment-stop address nodes) are dropped so they don't show as blank rows.
        setLocations(Array.isArray(all) ? selectablePointLocations(all) : []);
      } catch (err) {
        console.error("Failed to fetch locations:", err);
        setLocations([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };
    load();
  }, []);

  const handleGoToSettings = () => {
    setSettingsOpen(true);
    setActiveTab("yardSettings");
  };

  const handleYardSettingsSaved = useCallback(() => {
    setMyYardRefreshKey((k) => k + 1);
    setActiveTab("myYard");
  }, []);

  const handleCloseSettings = () => {
    setSettingsOpen(false);
    setActiveTab("myYard");
  };

  const handleOpenTrailer = (trailer) => {
    const existingTab = trailerTabs.find(
      (t) => t.trailer.trailerNumber === trailer.trailerNumber
    );
    if (existingTab) {
      setActiveTab(existingTab.id);
      return;
    }
    const id = `trailer-${trailer.trailerNumber}-${Date.now()}`;
    setTrailerTabs((prev) => [...prev, { id, trailer }]);
    setActiveTab(id);
  };

  const handleCloseTrailerTab = (id) => {
    setTrailerTabs((prev) => prev.filter((t) => t.id !== id));
    setActiveTab("overview");
  };

  const handleOpenCheckInTab = (appointment) => {
    if (!appointment) return;

    // LGX-1101: a walk-up draft has no appointmentId, so it can neither be required here nor
    // used to dedupe tabs — each blank check-in the guard starts is its own arrival.
    const isManualEntry = appointment.manualEntry === true;
    if (!isManualEntry && !appointment.appointmentId) return;

    if (!isManualEntry) {
      const existingTab = checkInTabs.find(
        (t) => t.appointment.appointmentId === appointment.appointmentId
      );
      if (existingTab) {
        setActiveTab(existingTab.id);
        return;
      }
    }

    const id = isManualEntry
      ? `checkin-walkup-${Date.now()}`
      : `checkin-${appointment.appointmentId}-${Date.now()}`;
    setCheckInTabs((prev) => [...prev, { id, appointment }]);
    setActiveTab(id);
  };

  const handleCloseCheckInTab = (id) => {
    setCheckInTabs((prev) => prev.filter((t) => t.id !== id));
    setActiveTab("overview");
  };

  const handleOpenCheckoutTab = (trailer) => {
    if (!trailer?.trailerNumber) return;
    const existingTab = checkoutTabs.find(
      (t) => t.trailer.trailerNumber === trailer.trailerNumber
    );
    if (existingTab) {
      setActiveTab(existingTab.id);
      return;
    }
    const id = `checkout-${trailer.trailerNumber}-${Date.now()}`;
    setCheckoutTabs((prev) => [...prev, { id, trailer }]);
    setActiveTab(id);
  };

  const handleCloseCheckoutTab = (id) => {
    setCheckoutTabs((prev) => prev.filter((t) => t.id !== id));
    setActiveTab("overview");
  };

  const handleTrailerMoveSuccess = () => {
    setTrailersRefreshKey((k) => k + 1);
    setMyYardRefreshKey((k) => k + 1);
  };

  const runYardSearch = () => {
    setYardSearchTrigger((n) => n + 1);
  };

  const handleYardStatusFilter = (value) => {
    setYardStatusFilter(value === "All Conditions" ? "All Conditions" : value);
  };

  const refreshAll = () => {
    setTrailersRefreshKey((k) => k + 1);
    setMyYardRefreshKey((k) => k + 1);
  };

  const tabContents = tabDefs.map((t) => {
    if (t.id === "overview") {
      return (
        <div key="overview" className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-hidden">
            <TrailersTab
              onOpenTrailer={handleOpenTrailer}
              onOpenCheckout={handleOpenCheckoutTab}
              refreshKey={trailersRefreshKey}
              hideToolbar
              externalSearchQuery={yardSearchQuery}
              onExternalSearchQueryChange={setYardSearchQuery}
              externalSearchTrigger={yardSearchTrigger}
              externalStatusFilter={yardStatusFilter}
              onExternalStatusFilterChange={setYardStatusFilter}
              onFilteredCountChange={setYardFilteredCount}
            />
          </div>
        </div>
      );
    }
    if (t.id === "myYard") {
      return (
        <div key="myYard" className="flex h-full min-h-0 flex-col">
          <MyYardTab
            locations={locations}
            isLoadingLocations={isLoadingLocations}
            onGoToSettings={handleGoToSettings}
            onOpenTrailer={handleOpenTrailer}
            refreshKey={myYardRefreshKey}
            autoRefreshEnabled={autoRefreshEnabled}
            refreshIntervalMs={refreshIntervalSec * 1000}
          />
        </div>
      );
    }
    if (t.id === "yardSettings") {
      return (
        <YardSettingsTab
          key="yardSettings"
          locations={locations}
          isLoadingLocations={isLoadingLocations}
          onClose={handleCloseSettings}
          onYardSettingsSaved={handleYardSettingsSaved}
        />
      );
    }
    const trailerEntry = trailerTabs.find((x) => x.id === t.id);
    if (trailerEntry) {
      return (
        <TrailerDetailView
          key={t.id}
          trailer={trailerEntry.trailer}
          onMoveSuccess={handleTrailerMoveSuccess}
          onOpenCheckout={handleOpenCheckoutTab}
        />
      );
    }
    const checkoutEntry = checkoutTabs.find((x) => x.id === t.id);
    if (checkoutEntry) {
      return (
        <TrailerDetailView
          key={t.id}
          trailer={checkoutEntry.trailer}
          mode="checkout"
          onClose={() => handleCloseCheckoutTab(t.id)}
          onCheckedOut={() => {
            refreshAll();
            handleCloseCheckoutTab(t.id);
          }}
        />
      );
    }
    const checkInEntry = checkInTabs.find((x) => x.id === t.id);
    if (checkInEntry) {
      return (
        <AppointmentCheckInFormModal
          key={t.id}
          appointment={checkInEntry.appointment}
          onClose={() => handleCloseCheckInTab(t.id)}
          onSubmitted={() => {
            refreshAll();
            handleCloseCheckInTab(t.id);
          }}
        />
      );
    }
    return <div key={t.id} />;
  });

  return (
    <div className="p-fluid-1 shadow-md flex h-full w-full min-h-0 flex-1 flex-col overflow-hidden font-roboto">
      <div className="ml-[2] flex flex-wrap items-center justify-between gap-2 rounded-md p-fluid-1">
        <div className="flex flex-wrap items-center gap-2">
            <FilterButton
              text={yardStatusFilter}
              options={STATUS_FILTER_OPTIONS}
              selectedValue={yardStatusFilter}
              onSelect={handleYardStatusFilter}
            />
            <SearchBox
              value={yardSearchQuery}
              onChange={setYardSearchQuery}
              onSearch={runYardSearch}
              placeholder="Search Trailers"
            />
            <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
              {yardFilteredCount} Trailers
            </span>
            <button
              type="button"
              onClick={() => {
                setToolbarReloading(true);
                refreshAll();
                window.setTimeout(() => setToolbarReloading(false), 400);
              }}
              className="rounded-full p-1 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30"
              title="Reload trailers"
              aria-label="Reload trailers"
            >
              <RefreshCw
                size={16}
                className={`text-gray-500 text-acs-light-text dark:text-acs-dark-text ${
                  toolbarReloading ? "animate-spin" : "hover:text-gray-700"
                }`}
              />
            </button>
          </div>
          {activeTab === "myYard" && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-acs-dark-subtext">
              <label className="flex cursor-pointer select-none items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={autoRefreshEnabled}
                  onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                  className="cursor-pointer"
                />
                Auto-refresh
              </label>
              {autoRefreshEnabled && (
                <select
                  value={refreshIntervalSec}
                  onChange={(e) => setRefreshIntervalSec(Number(e.target.value))}
                  className="rounded border border-gray-300 dark:border-acs-dark-border bg-white dark:bg-[#2A2D35] px-1 py-0.5 text-xs text-gray-700 dark:text-acs-dark-text"
                >
                  <option value={10}>10s</option>
                  <option value={20}>20s</option>
                  <option value={30}>30s</option>
                  <option value={60}>60s</option>
                  <option value={120}>2m</option>
                </select>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {canCheckInTrailer && (
              <Button text="Check In" onClick={() => setCheckInSearchOpen(true)} />
            )}
          </div>
      </div>

      <div className="mt-fluid-2 flex min-h-0 flex-1 flex-col scrollbar-hide">
        <Tabs
          moduleName="yard-management"
          tabs={tabDefs.map((x) => x.name)}
          tabContents={tabContents}
          activeTab={activeTabIndex}
          onTabChange={(index) => {
            const next = tabDefs[index];
            if (next) setActiveTab(next.id);
          }}
          isDeletable={tabDefs.map((_, i) => i >= DEFAULT_TABS_COUNT)}
          onRemoveTab={(index) => {
            const t = tabDefs[index];
            if (!t) return;
            if (t.id.startsWith("trailer-")) {
              handleCloseTrailerTab(t.id);
            } else if (t.id.startsWith("checkin-")) {
              handleCloseCheckInTab(t.id);
            } else if (t.id.startsWith("checkout-")) {
              handleCloseCheckoutTab(t.id);
            } else if (t.id === "yardSettings") {
              handleCloseSettings();
            }
          }}
        />
      </div>

      <YardCheckInSearchModal
        open={checkInSearchOpen}
        onClose={() => setCheckInSearchOpen(false)}
        onOpenCheckIn={handleOpenCheckInTab}
      />
    </div>
  );
};

export default YardManagement;
